import {useMemo, ReactNode} from "react";
import {ModalProps, Spinner, Text, VStack} from "@chakra-ui/react";
import {RequestState, RequestAction} from "../../models";
import {ConfirmationModal} from "../UI";

type VerificationProps = Omit<ModalProps, "children"> & {
    action?: RequestAction
    title?: string
    icon?: string
    children?: ReactNode
    hideIcon?: boolean
    iconColor?: string
    status: RequestState
};

function PendingContent() {
    return <div className="stack">
        <Text fontWeight="bold" color="dark.200" textAlign="center">
            Identity Verification Pending
        </Text>
        <Text textAlign="center" style={{textWrap: "pretty"}}>
            Please complete your identity verification using your device’s
            fingerprint or face scan on Admiral Access to proceed.
        </Text>
    </div>;
}

function FailedContent() {
    return <div className="stack">
        <Text fontWeight="bold" color="dark.200" textAlign="center">
            Identity Verification Failed
        </Text>
        <Text textAlign="center" style={{textWrap: "pretty"}}>
            please ensure your device supports fingerprint/face scan, and your internet is stable.<br/>
            Try again or contact support if needed.
        </Text>
    </div>;
}

function SuccessContent() {
    return <div className="stack">
        <Text fontWeight="bold" color="dark.200" textAlign="center">
            Identity Verification Successful
        </Text>
        <Text textAlign="center" style={{textWrap: "pretty"}}>
            Your identity has been successfully verified! <br/>
            For security, always ensure you’re the only one verifying your identity.
        </Text>
    </div>;
}
function ResolvedContent() {
    return <div className="stack">
        <Text fontWeight="bold" color="dark.200" textAlign="center">
            Request resolved
        </Text>
        <Text textAlign="center" style={{textWrap: "pretty"}}>
            This request is now closed. The outcome has been recorded.
        </Text>
    </div>;
}

function VerificationContent(p: {action?: RequestAction, state: RequestState}) {
    const types = ["aprove", "decline"];
    return <VStack>
        {p.state === "failed" && <FailedContent/>}
        {p.state === "verified" && !p.action && <SuccessContent/>}
        {p.state === "verified" && types.includes(p.action?.type ?? "") && <ResolvedContent/>}
        {p.state === "pending" && <>
            <PendingContent/>
            <Spinner color="primary.500" speed="0.65s" size="md"/>
        </>}
    </VStack>
}

export function VerificationModal(p: VerificationProps) {
    const desc = useMemo(function () {
        let res = "#8C8C8C";
        if (p.status === "failed") {
            res = "#ff3333";
        }
        if (p.status === "verified") {
            res = "#017172";
        }
        return res;
    }, [p.status]);
    return <ConfirmationModal
        {...p}
        description={<VerificationContent state={p.status}/>}
        iconColor={desc}
        icon={p.status === "verified" ? "checked_circle": "security"}
    />;
}

